import {
  Alert,
  AlertIcon,
  AlertTitle,
  TagProps,
  Wrap,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { ErrorMessage } from 'components/ErrorMessage';
import React, { Suspense } from 'react';
import { ErrorBoundary } from 'utils/errors';
import { graphql, useMutation, useSafeRelay } from 'utils/graphClient';
import { useFeatureFlag } from 'utils/sessionProvider';
import { useSearchModal } from '../../SearchModal/useSearchModal';
import { CreateTagInput } from './CreateTagInput';
import { Tag } from './Tag';
import { TagEditor_node_Query } from './__generated__/TagEditor_node_Query.graphql';
import { TagEditor_updateTags_Mutation } from './__generated__/TagEditor_updateTags_Mutation.graphql';

const query = graphql`
  query TagEditor_node_Query($id: ID!) {
    node(id: $id) {
      ... on HasTagsInterface {
        tags {
          id
          name
          originalName
        }
        canUpdateTags {
          value
        }
      }
    }
  }
`;

export const updateTagsMutation = graphql`
  mutation TagEditor_updateTags_Mutation($nodeId: ID!, $tags: [String!]!) {
    updateTags(input: { nodeId: $nodeId, tags: $tags }) {
      node {
        id
        ... on HasTagsInterface {
          tags {
            id
            name
            originalName
          }
        }
      }
    }
  }
`;

export type TagEditorProps = BaseTagEditorProps & TagProps;

export interface BaseTagEditorProps {
  nodeId: string;
  readOnly?: boolean;
  informOnEmpty?: boolean;
  skipAutoFocus?: boolean;
}

export const TagEditor: React.FC<TagEditorProps> = (props) => {
  return (
    <ErrorBoundary fallback={<TagsErrorPlaceholder />}>
      <Suspense>
        <UnsuspendedTagEditor {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};

const UnsuspendedTagEditor = ({
  nodeId,
  readOnly,
  informOnEmpty,
  skipAutoFocus = false,
  ...tagProps
}: TagEditorProps) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const { node } = useLazyLoadQuery<TagEditor_node_Query>(query, {
    id: nodeId,
  });
  const [updateTags, isUpdatingTags] =
    useMutation<TagEditor_updateTags_Mutation>(updateTagsMutation);
  const { openSearch } = useSearchModal();

  const isRenameTagsEnabled = useFeatureFlag('FEATURE_RENAME_TAGS_TO_LABELS');

  if (!node?.tags) {
    return <NoTagsPlaceholder />;
  }
  const { tags, canUpdateTags } = node;
  const tagNames = tags.map(({ originalName }) => originalName);
  const canEdit = !readOnly && !!canUpdateTags?.value;

  if (!canUpdateTags?.value && tagNames.length === 0 && informOnEmpty) {
    return <NoTagsPlaceholder />;
  }

  const onRemoveTag = (idToRemove: string) => {
    const filteredTags = tags
      .filter(({ id }) => id !== idToRemove)
      .map(({ originalName }) => originalName);
    updateTags({ variables: { nodeId, tags: filteredTags } });
  };

  return (
    <Wrap overflow="visible">
      {tags.map(({ id, originalName, name }) => {
        const onClickTag = () => {
          const searchQuery = isRenameTagsEnabled
            ? `label:${name}`
            : `tag:${name}`;
          openSearch(searchQuery);
        };
        return (
          <Tag
            onClick={onClickTag}
            onDelete={canEdit ? () => onRemoveTag(id) : undefined}
            key={id}
            {...tagProps}
          >
            {originalName}
          </Tag>
        );
      })}
      {canEdit && (
        <CreateTagInput
          onUpdateTags={(updatedTags: string[], onCompleted: () => void) => {
            updateTags({
              variables: { nodeId, tags: updatedTags },
              onCompleted,
            });
          }}
          tags={tagNames}
          isUpdatingTags={isUpdatingTags}
          skipAutoFocus={skipAutoFocus}
        />
      )}
    </Wrap>
  );
};

const NoTagsPlaceholder = () => {
  const isRenameTagsEnabled = useFeatureFlag('FEATURE_RENAME_TAGS_TO_LABELS');

  return (
    <Alert status="info">
      <AlertIcon />
      <AlertTitle>
        {isRenameTagsEnabled
          ? t`This item does not have any labels.`
          : t`This item does not have any tags.`}
      </AlertTitle>
    </Alert>
  );
};

const TagsErrorPlaceholder = () => {
  const isRenameTagsEnabled = useFeatureFlag('FEATURE_RENAME_TAGS_TO_LABELS');

  return (
    <ErrorMessage
      description={
        isRenameTagsEnabled ? t`Error loading labels.` : t`Error loading tags.`
      }
    />
  );
};
